import React, { useState, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { QueryLoader, toast } from "@jsq/ds/core";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import {
  useUpdateCaseProfileOwnersOrControllersCompletedMutation,
  useBulkUnlinkChildProfilesMutation,
  useGetProfilesForCaseQuery,
} from "../../graphql/supergraphHooks";
import { clientContext } from "../../utils/graphql_utils";
import { OwnerThreshold } from "../OwnerThreshold";
import { OwnerThresholdWarningModal } from "../OwnerThresholdWarningModal";
import { Profile } from "./types";
import { getInitialValues, getChildProfileNamesAndIds } from "./utils";
/**
 * Use the profile id from the URL. find the profile. If it is the investing profile use the childOwnershipThreshold for the investing profile.
 * If it is within the profiles[] find the profile and then find its parent profile, and use that childOwnershipThreshold,
 * presumably that could be the investing profile, but this allows for multi level.
 */
export const OwnerThresholdContainer = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const { rootProfileId, caseId, basePath } = useComplianceCaseContext();
  const isRoot = Number(profileId) === rootProfileId;
  const history = useHistory();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [subsidiaries, setSubsidiaries] = useState<string[]>([]);

  const [updateOwnersCompleted] = useUpdateCaseProfileOwnersOrControllersCompletedMutation({
    refetchQueries: ["GetComplianceCase", "GetProfilesForCase"],
    awaitRefetchQueries: true,
  });
  const [bulkUnlinkChildProfiles] = useBulkUnlinkChildProfilesMutation();

  const profileResults = useGetProfilesForCaseQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseId: Number(caseId),
    },
  });

  const complianceCase = profileResults.data?.complianceCase;

  const closePage = () => {
    history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
  };

  const { entityName, thresholdPercentage, areOwnersCompleted, hasOwners } = useMemo(() => {
    const empty = {
      entityName: "",
      thresholdPercentage: 0,
      areOwnersCompleted: undefined,
      hasOwners: undefined,
    };

    if (!complianceCase) {
      return empty;
    }

    const investingProfile = complianceCase.investingProfile;
    const profiles = complianceCase.profiles || [];

    // Find the target profile in caseResult
    const targetProfileInCase =
      profiles?.find((profile) => profile?.id === parseInt(profileId)) ||
      complianceCase?.investingProfile;

    const ownersCompleted = targetProfileInCase?.caseDataEntryStatus?.ownersCompleted;

    const hasOwners = (targetProfileInCase?.beneficialOwners?.length ?? 0) > 0;

    if (!targetProfileInCase) {
      return empty;
    }

    // Find the parent profile (could be investing profile or another profile)
    const parentProfile =
      profiles.find((profile) =>
        profile?.parentRelationships?.some(
          (rel) => rel.childProfileId === targetProfileInCase?.id,
        ),
      ) || investingProfile;

    return {
      entityName: targetProfileInCase.legalName ?? "",
      thresholdPercentage: parentProfile?.childOwnershipThreshold ?? 0,
      areOwnersCompleted: ownersCompleted,
      hasOwners: hasOwners,
    };
  }, [complianceCase, profileId]);

  const handleContinue = (thresholdMet: boolean) => {
    void (async () => {
      if (thresholdMet) {
        // If threshold is met (answer is yes),
        // Update owners completed to:
        // True if there are owners (Because we are then consistent)
        // False if there are no owners (Because need to collect them)
        await updateOwnersCompleted({
          variables: {
            caseId: Number(caseId),
            profileId: Number(profileId),
            ownersCompleted: hasOwners,
          },
        });
        // move forward to next screen
        history.push(`${basePath}/owners/${profileId}`);
      } else {
        // If threshold is not met (answer is no), update ownersCompleted to true
        // check if the answer is changing from yes to no + see if there are child profiles,
        // if so, show the warning modal, warning modal will call the mutation to unlink the profiles.
        // then update the ownersCompleted to true
        if (hasOwners) {
          const { names } = getChildProfileNamesAndIds(
            Number(profileId),
            profileResults.data?.complianceCase?.profiles as Profile[],
          );
          setSubsidiaries(names);
          setShowWarningModal(true);
        } else {
          await updateOwnersCompleted({
            variables: {
              caseId: Number(caseId),
              profileId: Number(profileId),
              ownersCompleted: true,
            },
          });
          closePage();
        }
      }
    })();
  };

  const handleUnlinkProfiles = async () => {
    try {
      // First unlink the child profiles
      await bulkUnlinkChildProfiles({
        variables: {
          profileId: Number(profileId),
          caseId: Number(caseId),
          profileType: "owner",
        },
      });

      // Then update owners completed status
      await updateOwnersCompleted({
        variables: {
          caseId: Number(caseId),
          profileId: Number(profileId),
          ownersCompleted: true,
        },
      });

      setShowWarningModal(false);
      history.push(isRoot ? basePath : `${basePath}/overview/${profileId}`);
    } catch (e) {
      toast.error("Failed to unlink profiles");
    }
  };

  return (
    <QueryLoader result={profileResults}>
      <OwnerThreshold
        thresholdPercentage={thresholdPercentage}
        entityName={entityName}
        initialValues={getInitialValues(areOwnersCompleted, !!hasOwners)}
        onContinue={handleContinue}
        onBack={closePage}
      />
      <OwnerThresholdWarningModal
        show={showWarningModal}
        title="Warning"
        parentName={entityName}
        subsidiaries={subsidiaries}
        onClose={() => setShowWarningModal(false)}
        onContinue={() => {
          void handleUnlinkProfiles();
        }}
      />
    </QueryLoader>
  );
};
